import { Typography } from '@material-ui/core';
import {
  Grid,
  GridColumn,
  GridDataStateChangeEvent,
  GridToolbar,
} from '@progress/kendo-react-grid';
import {
  ExcelExport,
  ExcelExportColumn,
  ExcelExportColumnGroup,
} from '@progress/kendo-react-excel-export';
import { process } from '@progress/kendo-data-query';
import * as React from 'react';

import { ComponentType } from '@/app/components/TemplateComponents/constants';
import ColumnMenu from '@/app/components/Kendo/ColumnMenu';
import translate from '@/app/utils/translate';

import KendoReportDownloadButton from '../../../components/KendoReportDownloadButton';
import { KendoTemplateResultsCell } from '../../../components/KendoTemplateResultsCell';
import KendoAvatarCell from '../../../components/KendoAvatarCell';

const empGridEvaluationData = () => [
  {
    cell: KendoAvatarCell,
    field: 'empId',
    show: true,
    title: translate.t('laPicture'),
    width: 88,
  },
  {
    field: 'name',
    show: true,
    title: translate.t('laName'),
    width: 150,
  },
  {
    field: 'position',
    show: true,
    title: translate.t('laPosition'),
    width: 150,
  },
  {
    field: 'empNumber',
    show: true,
    title: translate.t('laEmpNumber'),
    width: 150,
  },
  {
    cell: KendoTemplateResultsCell,
    editable: false,
    field: 'evalItems',
    filterable: false,
    groupable: false,
    show: true,
    sortable: false,
    title: translate.t('laEvaluationItems'),
  },
];

type ComponentResponseCommon = {
  componentId: string;
  id: number;
};

type CheckboxesComponentResponse = ComponentResponseCommon & {
  response: number[];
  type: ComponentType.CHECKBOXES;
};

type CompetencyLevelComponentResponse = ComponentResponseCommon & {
  response: any; // ???
  type: ComponentType.COMPETENCY_LEVEL;
};

type DateComponentResponse = ComponentResponseCommon & {
  response: string;
  type: ComponentType.DATE;
};

type DropdownComponentResponse = ComponentResponseCommon & {
  response: number[];
  type: ComponentType.DROPDOWN;
};

type EvaluationComponentResponseValue = {
  comment: string;
  managerEvaluation: number;
  optionId: number;
  selfEvaluation: number;
};

type EvaluationsComponentResponse = ComponentResponseCommon & {
  response: EvaluationComponentResponseValue[];
  type: ComponentType.EVALUATIONS;
};

type MultiselectDropdownComponentResponse = ComponentResponseCommon & {
  response: number[];
  type: ComponentType.MULTISELECT_DROPDOWN;
};

type ScaleComponentResponse = ComponentResponseCommon & {
  response: number;
  type: ComponentType.SCALE;
};

type TextComponentResponse = ComponentResponseCommon & {
  response: string;
  type: ComponentType.TEXT;
};

type ComponentResponse =
  | CheckboxesComponentResponse
  | CompetencyLevelComponentResponse
  | DateComponentResponse
  | DropdownComponentResponse
  | EvaluationsComponentResponse
  | MultiselectDropdownComponentResponse
  | ScaleComponentResponse
  | TextComponentResponse;

type ExportTemplateComponentCommon = {
  id: string;
  index: number;
  language: string;
  name: string;
};

type ExportColumnOption = {
  id: number;
  label: string;
  index: number;
};

type CheckboxesExportTemplateComponent = ExportTemplateComponentCommon & {
  options: ExportColumnOption[];
  type: ComponentType.CHECKBOXES;
};

type CompetencyLevelExportTemplateComponent = ExportTemplateComponentCommon & {
  options: ExportColumnOption[];
  type: ComponentType.COMPETENCY_LEVEL;
};

type DateExportTemplateComponent = ExportTemplateComponentCommon & {
  type: ComponentType.DATE;
};

type DropdownExportTemplateComponent = ExportTemplateComponentCommon & {
  options: ExportColumnOption[];
  type: ComponentType.DROPDOWN;
};

type EvaluationsExportTemplateComponent = ExportTemplateComponentCommon & {
  options: ExportColumnOption[];
  type: ComponentType.EVALUATIONS;
};

type FileExportTemplateComponent = ExportTemplateComponentCommon & {
  options: any; // ???
  type: ComponentType.FILE;
};

type MultiselectDropdownExportTemplateComponent = ExportTemplateComponentCommon & {
  options: ExportColumnOption[];
  type: ComponentType.MULTISELECT_DROPDOWN;
};

type ScaleExportTemplateComponent = ExportTemplateComponentCommon & {
  type: ComponentType.SCALE;
};

type TextExportTemplateComponent = ExportTemplateComponentCommon & {
  type: ComponentType.TEXT;
};

type ExportTemplateComponent =
  | CheckboxesExportTemplateComponent
  | CompetencyLevelExportTemplateComponent
  | DateExportTemplateComponent
  | DropdownExportTemplateComponent
  | EvaluationsExportTemplateComponent
  | FileExportTemplateComponent
  | MultiselectDropdownExportTemplateComponent
  | ScaleExportTemplateComponent
  | TextExportTemplateComponent;

type ExportTemplateSection = {
  components: ExportTemplateComponent[];
  empAccess: boolean;
  id: string;
  index: number;
  language: string;
  name: string;
  onlyEmpAccess: boolean;
  templateId: string;
};

type ExportTemplateStages = {
  default: boolean;
  id: number;
  index: number;
  isDeleted: boolean;
  label: string;
};

type ExportTemplate = {
  audience: {
    countryAudience: any[];
    roleAudience: any[];
    unitAudience: any[];
  };
  createdTime: string;
  deadline: Date;
  defaultLang: string;
  id: string;
  isTeam: boolean;
  language: string;
  sections: ExportTemplateSection[];
  stages: ExportTemplateStages[];
  status: string;
  subject: string;
  tags: any[];
  updatedBy: {
    id: number;
    employeeId: number;
    username: string;
  };
  updatedTIme: string;
};

type ExportData = {
  empId: number;
  empNumber: string;
  evalItems: {
    id: string;
    approvedBy: any[];
    archived: boolean;
    employeeId: number;
    respondentId: number;
    responses: ComponentResponse[];
    status: string;
    templateId: string;
    updatedBy: number;
    updatedTime: string;
  };
  hasAccount: boolean;
  name: string;
  position: string;
  selectedTemplateFields: any[];
  statusCode: string;
  template: ExportTemplate;
  templateId: string;
  unitId: number;
  unitName: string;
};

type Props = {
  empData: ExportData[];
  empGridTitle: string;
  exportFileName: string;
};

const mapSingularAppraisalResponse = (
  questionType: ComponentType | string,
  questionResponse: string,
  questionId: string,
): { [k: string]: string } => {
  switch (questionType) {
    case ComponentType.DATE:
    case ComponentType.TEXT:
    case ComponentType.SCALE:
      return {
        [questionId]: `${questionResponse}`,
      };
    default:
      return {};
  }
};

type EvaluationsQuestionResponse = {
  optionId: number;
  selfEvaluation: number;
  managerEvaluation: number;
  comment: string;
};

type CompetencyLevelQuestionResponse = {
  optionId: number;
  competencyLevel: number;
  targetLevel: number;
  comment: string;
};

const getFormattedAppraisalResponse = (
  questionType: ComponentType | string,
  questionId: string,
  optionId: string,
  questionResponse?:
    | string
    | EvaluationsQuestionResponse
    | CompetencyLevelQuestionResponse,
) => {
  const resolveVariableValue = (variableName: string) => {
    return questionResponse &&
      typeof questionResponse !== 'string' &&
      questionResponse[variableName]
      ? questionResponse[variableName]
      : '';
  };

  switch (questionType) {
    case ComponentType.CHECKBOXES:
      return {
        [`${questionId}-${optionId}`]: questionResponse ? 'x' : '',
      };
    case ComponentType.EVALUATIONS:
      return {
        [`${questionId}-${optionId}-self`]: resolveVariableValue(
          'selfEvaluation',
        ),
        [`${questionId}-${optionId}-manager`]: resolveVariableValue(
          'managerEvaluation',
        ),
        [`${questionId}-${optionId}-comment`]: resolveVariableValue('comment'),
      };
    case ComponentType.COMPETENCY_LEVEL:
      return {
        [`${questionId}-${optionId}-competency`]: resolveVariableValue(
          'competencyLevel',
        ),
        [`${questionId}-${optionId}-target`]: resolveVariableValue(
          'targetLevel',
        ),
        [`${questionId}-${optionId}-comment`]: resolveVariableValue('comment'),
      };
    default:
      throw new Error(`Can't handle question type: ${questionType}`);
  }
};

const unbundleGroupedAppraisalResponses = (
  questionType: ComponentType,
  questionGroupedResponses: any,
  questionId: string,
  questionFallback?: any,
): { [k: string]: string } => {
  if (questionType === ComponentType.FILE) {
    return {
      [questionId]: questionGroupedResponses
        ? questionGroupedResponses.attachmentName
        : '',
    };
  }

  const hasResponses =
    questionGroupedResponses && Array.isArray(questionGroupedResponses);
  if (questionFallback && !hasResponses) {
    return questionFallback.reduce(
      (acc: { [k: string]: string }, questionOption: any) => ({
        ...acc,
        ...getFormattedAppraisalResponse(
          questionType,
          questionId,
          questionOption.id,
        ),
      }),
      {},
    );
  } else if (!hasResponses) {
    return {};
  }

  return questionGroupedResponses.reduce(
    (acc: { [k: string]: string }, questionResponse: any) => ({
      ...acc,
      ...getFormattedAppraisalResponse(
        questionType,
        questionId,
        [ComponentType.EVALUATIONS, ComponentType.COMPETENCY_LEVEL].includes(
          questionType,
        )
          ? questionResponse.optionId
          : questionResponse,
        questionResponse,
      ),
    }),
    {},
  );
};

const mapDropdownResponse = (
  componentResponse: DropdownComponentResponse,
  exportDataItem: ExportData,
  appraisalRespColumnId: string,
): { [k: string]: string } => {
  const components = exportDataItem.template.sections
    .map((section: any) => section.components)
    .flat();
  const component = components.find(
    (comp: any) => comp.id === componentResponse.componentId,
  );
  const option = component.options.find(
    (optn: any) => optn.id === componentResponse.response[0],
  );

  return {
    [appraisalRespColumnId]: !!option ? option.label : '',
  };
};
const mapMultiselectDropdownResponse = (
  componentResponse: MultiselectDropdownComponentResponse,
  exportDataItem: ExportData,
  appraisalRespColumnId: string,
): { [k: string]: string } => {
  const components = exportDataItem.template.sections
    .map((section: any) => section.components)
    .flat();
  const component = components.find(
    (comp: any) => comp.id === componentResponse.componentId,
  );
  const options = componentResponse.response.reduce((acc, optId) => {
    const compOption = component.options.find((optn: any) => optn.id === optId);
    if (!!compOption) {
      acc.push(compOption);
    }

    return acc;
  }, []);

  return {
    [appraisalRespColumnId]: options.map(opt => opt.label).join(', '),
  };
};

const getSortedComponentIds = (
  empData: ExportData,
): { sectionIds: string[]; componentIds: string[] } => {
  const sortedSections = empData.template.sections
    .slice()
    .sort((a, b) => a.index - b.index);
  const sortedComponents = sortedSections
    .map(section => {
      return section.components.sort((a, b) => a.index - b.index);
    })
    .flat();

  return {
    sectionIds: sortedSections.map(s => s.id),
    componentIds: sortedComponents.map(c => c.id),
  };
};

const mapExportData = (
  exportData: ExportData[],
  questionExportColumns: ExportTemplateComponent[],
) =>
  exportData.map((exportDataItem: ExportData) => {
    const { componentIds } = getSortedComponentIds(exportDataItem);
    const sortedColumns = questionExportColumns
      .slice()
      .sort((a, b) =>
        componentIds.indexOf(a.id) > componentIds.indexOf(b.id) ? 1 : -1,
      );

    return sortedColumns.reduce(
      (acc: any, column) => {
        const found = exportDataItem.evalItems.responses.find(
          resp => resp.componentId === column.id,
        );

        const ensureFoundResponse = <T extends ComponentResponse>(
          foundResponse: ComponentResponse | undefined,
        ): T => {
          return !!foundResponse
            ? (foundResponse as T)
            : (({
                componentId: column.id,
                response: [],
                type: column.type,
              } as unknown) as T);
        };

        switch (column.type) {
          case ComponentType.CHECKBOXES:
          case ComponentType.COMPETENCY_LEVEL:
          case ComponentType.EVALUATIONS:
          case ComponentType.FILE: {
            const unbundledGroupAnswers = unbundleGroupedAppraisalResponses(
              column.type,
              found ? found.response : '',
              column.id,
              found
                ? undefined
                : (column.options as ExportColumnOption[] | any),
            );
            return {
              ...acc,
              ...unbundledGroupAnswers,
            };
          }
          case ComponentType.DROPDOWN: {
            const dropdownMappedValues = mapDropdownResponse(
              ensureFoundResponse<DropdownComponentResponse>(found),
              exportDataItem,
              column.id,
            );

            return {
              ...acc,
              ...dropdownMappedValues,
            };
          }
          case ComponentType.MULTISELECT_DROPDOWN: {
            const multiselectDropdownMappedValues = mapMultiselectDropdownResponse(
              ensureFoundResponse<MultiselectDropdownComponentResponse>(found),
              exportDataItem,
              column.id,
            );

            return {
              ...acc,
              ...multiselectDropdownMappedValues,
            };
          }
          case ComponentType.DATE:
          case ComponentType.TEXT:
          case ComponentType.SCALE:
          default: {
            const singularResponse = mapSingularAppraisalResponse(
              column.type,
              found ? `${found.response}` : '',
              column.id,
            );

            return {
              ...acc,
              ...singularResponse,
            };
          }
        }
      },
      {
        ...exportDataItem,
        sheets: { rows: { cells: { wrap: true } } },
      },
    );
  });

const mapExportGridHeader = (column: any) => {
  switch (column.type) {
    case ComponentType.CHECKBOXES: {
      const colOptionComponents = column.options
        .sort((a: any, b: any) => a.index - b.index)
        .map((colOption: any) => {
          return (
            <ExcelExportColumn
              key={`${column.id}-${colOption.id}`}
              field={`${column.id}-${colOption.id}`}
              title={colOption.label}
            />
          );
        });

      return (
        <ExcelExportColumnGroup
          key={column.id}
          title={column.name}
          headerCellOptions={{
            textAlign: 'center',
          }}
        >
          {column.options.length === 1
            ? colOptionComponents[0]
            : colOptionComponents}
        </ExcelExportColumnGroup>
      );
    }
    case ComponentType.EVALUATIONS: {
      const colGroupComponents = column.options.map((colOption: any) => (
        <ExcelExportColumnGroup
          key={`${column.id}-${colOption.id}`}
          title={colOption.label}
          headerCellOptions={{
            textAlign: 'center',
          }}
        >
          <ExcelExportColumn
            key={`${column.id}-${colOption.id}-self`}
            field={`${column.id}-${colOption.id}-self`}
            title={translate.t('laSelfEvaluation')}
          />
          <ExcelExportColumn
            key={`${column.id}-${colOption.id}-manager`}
            field={`${column.id}-${colOption.id}-manager`}
            title={translate.t('laMgrEvaluation')}
          />
          <ExcelExportColumn
            key={`${column.id}-${colOption.id}-comment`}
            field={`${column.id}-${colOption.id}-comment`}
            title={translate.t('laComment')}
          />
        </ExcelExportColumnGroup>
      ));

      return (
        <ExcelExportColumnGroup
          key={column.id}
          title={column.name}
          headerCellOptions={{
            textAlign: 'center',
          }}
        >
          {column.options.length === 1
            ? colGroupComponents[0]
            : colGroupComponents}
        </ExcelExportColumnGroup>
      );
    }
    case ComponentType.COMPETENCY_LEVEL: {
      const colGroupComponents = column.options.map((colOption: any) => (
        <ExcelExportColumnGroup
          key={`${column.id}-${colOption.id}`}
          title={colOption.label}
          headerCellOptions={{
            textAlign: 'center',
          }}
        >
          <ExcelExportColumn
            key={`${column.id}-${colOption.id}-competency`}
            field={`${column.id}-${colOption.id}-competency`}
            title={translate.t('laSkillLevel')}
          />
          <ExcelExportColumn
            key={`${column.id}-${colOption.id}-target`}
            field={`${column.id}-${colOption.id}-target`}
            title={translate.t('laTargetLevel')}
          />
          <ExcelExportColumn
            key={`${column.id}-${colOption.id}-comment`}
            field={`${column.id}-${colOption.id}-comment`}
            title={translate.t('laComment')}
          />
        </ExcelExportColumnGroup>
      ));

      return (
        <ExcelExportColumnGroup
          key={column.id}
          title={column.name}
          headerCellOptions={{
            textAlign: 'center',
          }}
        >
          {column.options.length === 1
            ? colGroupComponents[0]
            : colGroupComponents}
        </ExcelExportColumnGroup>
      );
    }
    default:
      return (
        <ExcelExportColumn
          key={column.id}
          field={column.id}
          title={column.name}
        />
      );
  }
};

const EmployeeAppraisalGrid = (props: Props) => {
  const { exportFileName, empData, empGridTitle } = props;
  const [columns, setColumns] = React.useState<any[]>(empGridEvaluationData());
  const [dataState = { skip: 0, take: 9 }, setDataState] = React.useState();
  const exportRef: React.LegacyRef<ExcelExport> = React.useRef();
  const gridRef: React.LegacyRef<Grid> = React.useRef();

  const onColumnsSubmit = (columnsState: any) => {
    setColumns(columnsState);
  };

  const createDataState = (dataStateProp: any) => {
    return {
      dataState: setDataState(dataStateProp),
    };
  };

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    return createDataState(event.data);
  };

  const getQuestionExportColumns = (): ExportTemplateComponent[] => {
    const resolveSelected = () => {
      if (empData && empData.length) {
        const dataItem = empData[0];
        const selectedSectionKeys: string[] = Object.keys(
          dataItem.selectedTemplateFields,
        );
        const { componentIds, sectionIds } = getSortedComponentIds(dataItem);

        const sortedSelectedSectionIds = sectionIds.filter(sId =>
          selectedSectionKeys.includes(sId),
        );

        return {
          sortedAllComponentIds: componentIds,
          selectedSectionIds: sortedSelectedSectionIds,
        };
      }

      return {
        sortedAllComponentIds: [],
        selectedSectionIds: [],
      };
    };

    const { selectedSectionIds, sortedAllComponentIds } = resolveSelected();

    return selectedSectionIds.reduce(
      (
        acc: ExportTemplateComponent[],
        sectionId: string,
      ): ExportTemplateComponent[] => {
        if (!empData || !empData[0] || !empData[0].template.sections) {
          return acc;
        }

        const foundSection = empData[0].template.sections.find(section => {
          return section.id === sectionId;
        });

        if (!foundSection) {
          return acc;
        }

        const extraColumns = foundSection.components
          .filter(comp =>
            empData[0].selectedTemplateFields[
              sectionId
            ].selectedFields.includes(comp.id),
          )
          .sort((a, b) =>
            sortedAllComponentIds.indexOf(a.id) >
            sortedAllComponentIds.indexOf(b.id)
              ? 1
              : -1,
          );

        return acc.concat(extraColumns);
      },
      [],
    );
  };

  const getNormalExcelExportColumns = () =>
    (gridRef.current ? gridRef.current.columns : columns)
      .filter(col => col.field !== 'evalItems')
      .map(col => {
        const title =
          col.field === 'empId' ? translate.t('laEmpId') : col.title;

        return (
          <ExcelExportColumn key={col.field} field={col.field} title={title} />
        );
      });

  const getGroupedExcelExportColumns = () =>
    getQuestionExportColumns().map(mapExportGridHeader);

  const saveGridData = () => {
    const questionExportColumns = getQuestionExportColumns();
    const exportData = mapExportData(
      process(empData, {
        ...dataState,
        // Export all the records
        skip: 0,
        take: undefined,
      }).data,
      questionExportColumns,
    );

    exportRef.current.save(exportData);
  };

  return (
    <>
      <ExcelExport ref={exportRef} fileName={exportFileName}>
        {getNormalExcelExportColumns()}
        {getGroupedExcelExportColumns()}
      </ExcelExport>
      <Grid
        data={process(empData, dataState)}
        onDataStateChange={dataStateChange}
        {...dataState}
        sortable={true}
        resizable={true}
        pageable={true}
        groupable={true}
        filterable={false}
        ref={gridRef}
      >
        <GridToolbar>
          <Typography variant="h6" gutterBottom color="primary">
            {empGridTitle}
          </Typography>
          <KendoReportDownloadButton onClick={saveGridData} />
        </GridToolbar>
        {columns.map(
          (column: any, idx: number) =>
            column.show && (
              <GridColumn
                {...column}
                key={idx}
                columnMenu={
                  column.field !== 'evalItems'
                    ? (otherProps: any) => (
                        <ColumnMenu
                          {...otherProps}
                          columns={columns}
                          onColumnsSubmit={onColumnsSubmit}
                        />
                      )
                    : null
                }
              />
            ),
        )}
      </Grid>
    </>
  );
};

export default EmployeeAppraisalGrid;
